import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../api";
import { Nav } from "../components/Nav";

const INPUT_CLS =
  "w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 rounded-none transition-colors";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password !== confirm) {
      setError("Passwords don't match");
      return;
    }
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="reset-password-page" className="bg-[#050505] text-white min-h-screen">
      <Nav />
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="px-6 md:px-12 pt-44 md:pt-52 pb-24 max-w-lg"
      >
        <p className="text-violet-400 text-xs tracking-[0.35em] uppercase mb-4">Account Recovery</p>
        <h1 className="font-display uppercase tracking-tighter text-5xl md:text-6xl leading-[0.95] mb-10">
          Reset <span className="text-outline-purple">Password</span>
        </h1>

        {!token ? (
          <p data-testid="reset-no-token" className="text-white/50 text-sm">
            This link is missing its token. Request a fresh one from the sign-in box.
          </p>
        ) : done ? (
          <div data-testid="reset-success">
            <p className="text-white/70">Your password is updated.</p>
            <Link
              to="/forum?signin=1"
              data-testid="reset-signin-link"
              className="inline-block mt-6 bg-primary text-white px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:bg-[#FF7733] transition-colors"
            >
              Sign In
            </Link>
          </div>
        ) : (
          <form data-testid="reset-password-form" onSubmit={submit} className="space-y-5">
            <input
              data-testid="reset-password-input"
              required
              type="password"
              minLength={6}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="New password (6+ characters)"
              className={INPUT_CLS}
            />
            <input
              data-testid="reset-confirm-input"
              required
              type="password"
              minLength={6}
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Confirm new password"
              className={INPUT_CLS}
            />
            {error && (
              <p data-testid="reset-error" className="text-sm text-red-400 border border-red-400/30 bg-red-400/10 px-4 py-3">{error}</p>
            )}
            <button
              type="submit"
              data-testid="reset-submit-btn"
              disabled={busy}
              className="bg-primary text-white px-8 py-4 uppercase tracking-widest text-sm font-semibold hover:bg-[#FF7733] transition-colors disabled:opacity-50"
            >
              {busy ? "Updating..." : "Set New Password"}
            </button>
          </form>
        )}
      </motion.div>
    </div>
  );
}
